import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Download, ShieldCheck, Calendar, FileText, X } from "lucide-react";
import axios from "axios";
import { AppHeader } from "../components/AppHeader";
import { formatDate } from "../lib/fleet";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const publicApi = axios.create({ baseURL: `${BASE_URL}/api`, timeout: 30000 });

// Public share pages carry a customer's plate number and, potentially,
// documents that were shared to a specific person via a bearer-token URL.
// They MUST never appear in a search index. Backend adds ``X-Robots-Tag:
// noindex, nofollow, noarchive`` on the JSON responses; we mirror the
// signal on the HTML document via a <meta name=robots> tag installed at
// mount time and cleanly removed on unmount so navigating away from
// /share/:token doesn't leave the whole app marked ``noindex``.
function useNoIndexMeta() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow, noarchive");
    meta.setAttribute("data-mf-share-noindex", "true");
    document.head.appendChild(meta);
    // Neutralise any canonical set by a previous page — a public share
    // must never claim to be the canonical URL of anything.
    const prevCanonical = document.querySelector('link[rel="canonical"]');
    const prevCanonicalHref = prevCanonical?.getAttribute("href") || null;
    if (prevCanonical) prevCanonical.setAttribute("href", "");
    return () => {
      meta.remove();
      if (prevCanonical && prevCanonicalHref !== null) {
        prevCanonical.setAttribute("href", prevCanonicalHref);
      }
    };
  }, []);
}

export default function PublicShare() {
  useNoIndexMeta();
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, share: null, error: null });
  const [openDoc, setOpenDoc] = useState(null); // {intervention, dataUrl}
  const [loadingDocId, setLoadingDocId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/shares/${token}`);
        setState({ loading: false, share: data, error: null });
      } catch (e) {
        const s = e?.response?.status;
        setState({
          loading: false,
          share: null,
          error: s === 410 ? "expired" : s === 404 ? "invalid" : "network",
        });
      }
    })();
  }, [token]);

  const openDocument = useCallback(async (doc) => {
    setLoadingDocId(doc.id);
    try {
      const { data } = await publicApi.get(`/public/shares/${token}/documents/${doc.id}`);
      if (!data.invoice_photo) {
        toast.error("Le téléchargement n'est pas autorisé pour ce document.");
        return;
      }
      setOpenDoc({ intervention: doc, dataUrl: data.invoice_photo, allowDownload: data.allow_download });
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoadingDocId(null);
    }
  }, [token]);

  const download = () => {
    if (!openDoc?.dataUrl) return;
    const a = document.createElement("a");
    a.href = openDoc.dataUrl;
    const safe = (openDoc.intervention.title || "document")
      .replace(/[^a-zA-Z0-9-_ ]/g, "").trim() || "document";
    a.download = `${safe}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--mf-bg)", display: "flex", flexDirection: "column" }}>
      <AppHeader background="surface" clickable={false} />
      <main style={{ flex: 1, padding: "24px 16px calc(60px + env(safe-area-inset-bottom))", maxWidth: 640, margin: "0 auto", width: "100%" }}>
        {state.loading && (
          <div style={{ padding: 60, textAlign: "center" }} data-testid="public-share-loading">
            <div style={{
              display: "inline-block", width: 28, height: 28, borderRadius: 14,
              border: "3px solid var(--mf-border)", borderTopColor: "var(--mf-brand)",
              animation: "mf-spin 700ms linear infinite",
            }} />
            <style>{`@keyframes mf-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {!state.loading && state.error && (
          <EmptyMessage
            testID={`public-share-${state.error}`}
            title={state.error === "expired" ? "Lien expiré ou révoqué" : state.error === "invalid" ? "Lien invalide" : "Impossible de charger"}
            subtitle={state.error === "expired"
              ? "Ce lien de partage n'est plus actif. Demandez au propriétaire de vous en envoyer un nouveau."
              : state.error === "invalid"
                ? "Ce lien n'existe pas ou n'est plus valide."
                : "Vérifiez votre connexion et réessayez."}
          />
        )}

        {state.share && (
          <div className="mf-anim-in">
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
                borderRadius: 999, background: "rgba(11,25,44,0.08)", color: "var(--mf-brand)",
                fontSize: 12, fontWeight: 700, marginBottom: 12,
              }}>
                <ShieldCheck size={14} />
                Consultation en lecture seule
              </div>
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.4 }}>
                {state.share.vehicle.brand} {state.share.vehicle.model}
              </h1>
              <div style={{ marginTop: 6, fontSize: 13, color: "var(--mf-text-secondary)", fontWeight: 600, letterSpacing: 1 }}>
                {state.share.vehicle.plate?.toUpperCase()}
                {state.share.vehicle.year ? ` · ${state.share.vehicle.year}` : ""}
              </div>
              {state.share.recipient_label && (
                <div style={{ marginTop: 10, fontSize: 14, color: "var(--mf-text-muted)" }}>
                  Partagé avec : <strong>{state.share.recipient_label}</strong>
                </div>
              )}
              {state.share.expires_at && (
                <div style={{ marginTop: 4, fontSize: 12, color: "var(--mf-text-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Calendar size={12} /> Expire le {formatDate(state.share.expires_at)}
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="public-share-docs">
              {state.share.documents.map((d) => (
                <button
                  key={d.id}
                  onClick={() => openDocument(d)}
                  disabled={loadingDocId === d.id}
                  data-testid={`public-doc-${d.id}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: 16, borderRadius: 16, border: "none", background: "var(--mf-bg-card)",
                    boxShadow: "var(--mf-shadow)", cursor: loadingDocId === d.id ? "wait" : "pointer",
                    textAlign: "left", opacity: loadingDocId === d.id ? 0.7 : 1,
                    transition: "transform 200ms, box-shadow 200ms",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--mf-shadow-strong)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--mf-shadow)"; }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: "rgba(11,25,44,0.07)", color: "var(--mf-brand)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <FileText size={22} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)" }}>{d.title}</div>
                    <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>
                      {d.category_label}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2 }}>
                      {d.expiration_date ? `Expire le ${formatDate(d.expiration_date)}` : d.date ? `Émis le ${formatDate(d.date)}` : ""}
                    </div>
                  </div>
                  <Eye size={18} color="var(--mf-text-muted)" />
                </button>
              ))}
            </div>

            <div style={{
              marginTop: 32, padding: 16, background: "var(--mf-bg-muted)", borderRadius: 12,
              fontSize: 12, color: "var(--mf-text-muted)", lineHeight: 1.6, textAlign: "center",
            }}>
              Ce lien vous a été partagé par un utilisateur My Fleet.
              Il ne donne accès qu'aux documents ci-dessus, pour ce véhicule uniquement.
            </div>
          </div>
        )}
      </main>

      {openDoc && (
        <div
          onClick={() => setOpenDoc(null)}
          data-testid="public-doc-viewer"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20,
          }}
        >
          <div style={{ position: "absolute", top: 20, right: 20, display: "flex", gap: 8 }}>
            {openDoc.allowDownload && (
              <button
                onClick={(e) => { e.stopPropagation(); download(); }}
                data-testid="public-doc-download"
                style={{
                  padding: "10px 16px", borderRadius: 999, background: "rgba(255,255,255,0.15)",
                  border: "none", color: "#fff", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 14,
                }}
              >
                <Download size={16} /> Télécharger
              </button>
            )}
            <button
              onClick={() => setOpenDoc(null)}
              aria-label="Fermer"
              data-testid="public-doc-close"
              style={{
                width: 40, height: 40, borderRadius: 20, background: "rgba(255,255,255,0.15)",
                border: "none", color: "#fff", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <X size={22} />
            </button>
          </div>
          <img
            src={openDoc.dataUrl}
            alt={openDoc.intervention.title}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "88vh", objectFit: "contain" }}
          />
        </div>
      )}
    </div>
  );
}

function EmptyMessage({ title, subtitle, testID }) {
  return (
    <div data-testid={testID} style={{
      padding: "60px 20px", textAlign: "center",
    }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--mf-text)" }}>{title}</h2>
      <p style={{ margin: "8px 0 0", color: "var(--mf-text-secondary)", fontSize: 14, lineHeight: 1.5 }}>{subtitle}</p>
    </div>
  );
}
